import React, { useState, useEffect } from 'react';
import {
  Table, Button, Badge, Modal, Form, Row, Col, Alert, Accordion
} from 'react-bootstrap';
import { FaPlus, FaEdit, FaDownload, FaTrash } from 'react-icons/fa';
import axios from 'axios';

const GdprCrossBorderTransfersTab = ({ userData, onScoreUpdate, showNotification, region, refreshActivities, logToAccessLogs }) => {
  const [transfers, setTransfers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState(null);
  const [newTransfer, setNewTransfer] = useState({
    type: 'International Transfer',
    source: '',
    destination: '',
    entity: '',
    legalMechanism: '',
    dataCategories: [],
    adequacyDecision: false,
    sccsUsed: false,
    dpaApproval: false,
    tiaFileKey: ''
  });

  const headers = {
    Region: region,
    UserId: userData?.['cognito:username']
  };

  const LEGAL_MECHANISMS = [
    'Adequacy Decision', 'Standard Contractual Clauses (SCCs)',
    'Binding Corporate Rules (BCRs)', 'Code of Conduct',
    'Certification Mechanism', 'Explicit Consent',
    'Contract Performance', 'Public Interest',
    'Legal Claims', 'Vital Interests'
  ];

  const EU_EEA_COUNTRIES = [
    'Austria', 'Belgium', 'Bulgaria', 'Croatia', 'Cyprus', 'Czech Republic',
    'Denmark', 'Estonia', 'Finland', 'France', 'Germany', 'Greece', 'Hungary',
    'Ireland', 'Italy', 'Latvia', 'Lithuania', 'Luxembourg', 'Malta',
    'Netherlands', 'Poland', 'Portugal', 'Romania', 'Slovakia', 'Slovenia',
    'Spain', 'Sweden', 'Iceland', 'Liechtenstein', 'Norway'
  ];

  const ADEQUACY_COUNTRIES = [
    'Andorra', 'Argentina', 'Canada (commercial organizations)',
    'Faroe Islands', 'Guernsey', 'Israel', 'Isle of Man', 'Japan',
    'Jersey', 'New Zealand', 'Republic of Korea', 'Switzerland',
    'United Kingdom', 'Uruguay'
  ];

  useEffect(() => {
    fetchTransfers();
  }, []);

  const fetchTransfers = async () => {
    try {
      const res = await axios.get(
        'https://uz4mb83j99.execute-api.ap-southeast-2.amazonaws.com/prod/api/gdpr/cross-border-transfers/list',
        { headers }
      );
      setTransfers(res.data);
      calculateComplianceScore(res.data);
    } catch (error) {
      console.error('Error fetching transfers:', error);
    }
  };

  const calculateComplianceScore = (transfersList) => {
    if (transfersList.length === 0) { onScoreUpdate(0); return; }
    let score = 100;
    transfersList.forEach(transfer => {
      const isEUEEA = EU_EEA_COUNTRIES.includes(transfer.destination);
      const hasAdequacy = ADEQUACY_COUNTRIES.includes(transfer.destination);
      if (isEUEEA) return;
      if (hasAdequacy && transfer.adequacyDecision) return;
      if (!transfer.legalMechanism) score -= 25;
      else if (transfer.legalMechanism === 'Standard Contractual Clauses (SCCs)' && !transfer.sccsUsed) score -= 15;
      else if (!transfer.dpaApproval && ['Binding Corporate Rules (BCRs)', 'Code of Conduct'].includes(transfer.legalMechanism)) score -= 10;
      if (!transfer.dataCategories || transfer.dataCategories.length === 0) score -= 10;
    });
    onScoreUpdate(Math.max(0, Math.round(score)));
  };

  const logActivity = async (message, badge = 'info') => {
    try {
      const payload = { action: badge, module: 'Cross Border Transfers', details: message };
      await axios.post(
        'https://w8wvx2z3f4.execute-api.ap-southeast-2.amazonaws.com/prod/api/activity-log/save',
        payload,
        { headers }
      );
      if (refreshActivities) await refreshActivities();
    } catch (err) {
      console.warn('Failed to log activity:', err.message);
    }
  };

  const handleSave = async () => {
    try {
      const payload = { ...newTransfer };

      if (editingTransfer) {
        await axios.put(
          `https://b8zenf9lt8.execute-api.ap-southeast-2.amazonaws.com/prod/api/gdpr/cross-border-transfers/update/${editingTransfer.id}`,
          payload,
          { headers }
        );
        await logActivity(`Updated cross-border transfer to ${payload.destination}`, 'warning');
        logToAccessLogs?.({
          action:      'Transfer Updated',
          severity:    'warning',
          description: `Updated cross-border transfer to ${newTransfer.destination} â€” ${newTransfer.entity}`,
          metadata: {
            destination:   newTransfer.destination,
            source:        newTransfer.source,
            entity:        newTransfer.entity,
            legalMechanism: newTransfer.legalMechanism,
            assetId:       editingTransfer.id,
          }
        });
      } else {
        await axios.post(
          'https://aaywh7ik0l.execute-api.ap-southeast-2.amazonaws.com/prod/api/gdpr/cross-border-transfers/create',
          payload,
          { headers }
        );
        await logActivity(`Added new cross-border transfer to ${payload.destination}`, 'info');
        logToAccessLogs?.({
          action:      'Transfer Created',
          severity:    'success',
          description: `Created cross-border transfer to ${newTransfer.destination} â€” ${newTransfer.entity}`,
          metadata: {
            destination:    newTransfer.destination,
            source:         newTransfer.source,
            entity:         newTransfer.entity,
            legalMechanism: newTransfer.legalMechanism,
          }
        });
      }

      showNotification('Transfer record saved successfully!');
      fetchTransfers();
      resetForm();
    } catch (error) {
      console.error('Error saving transfer:', error);
      showNotification('Error saving transfer record', 'danger');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this transfer record?')) {
      try {
        await axios.delete(
          `https://7mqfvli800.execute-api.ap-southeast-2.amazonaws.com/prod/api/gdpr/cross-border-transfers/delete/${id}`,
          { headers }
        );
        await logActivity('Deleted cross-border transfer record', 'danger');
        logToAccessLogs?.({
          action:      'Transfer Deleted',
          severity:    'critical',
          description: `Deleted cross-border transfer record ID: ${id}`,
          metadata: { assetId: id }
        });
        showNotification('Transfer record deleted successfully!');
        fetchTransfers();
      } catch (error) {
        console.error('Error deleting transfer:', error);
        showNotification('Error deleting transfer record', 'danger');
      }
    }
  };

  const downloadTIA = async (transferId, fileName) => {
    try {
      const res = await axios.get(
        `https://5g08n1wgyk.execute-api.ap-southeast-2.amazonaws.com/prod/api/gdpr/cross-border-transfers/download-tia/${transferId}`,
        { headers, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || `TIA_${transferId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      await logActivity(`Downloaded TIA for transfer ${transferId}`, 'primary');
    } catch (error) {
      console.error('Error downloading TIA:', error);
      showNotification('Error downloading TIA document', 'danger');
    }
  };

  const resetForm = () => {
    setNewTransfer({
      type: 'International Transfer', source: '', destination: '',
      entity: '', legalMechanism: '', dataCategories: [],
      adequacyDecision: false, sccsUsed: false, dpaApproval: false, tiaFileKey: ''
    });
    setEditingTransfer(null);
    setShowModal(false);
  };


const [activeAccordionKey, setActiveAccordionKey] = useState(null);

  return (
    <>
     <div className="border rounded">
  <div
    className="d-flex justify-content-between align-items-center p-3"
    style={{ cursor: "pointer" }}
    onClick={() =>
      setActiveAccordionKey(activeAccordionKey === "0" ? null : "0")
    }
  >
    <h6>Cross-Border Data Transfers</h6>
    <span>{activeAccordionKey === "0" ? "â–²" : "â–¼"}</span>
  </div>

  {activeAccordionKey === "0" && (
    <div className="p-3 border-top">
            <Row className="mb-3">
              <Col>
                <Button variant="primary" onClick={() => setShowModal(true)}>
                  <FaPlus /> Register New Transfer
                </Button>
              </Col>
            </Row>

            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Type</th>
                  <th>Source â†’ Destination</th>
                  <th>Organization</th>
                  <th>Legal Mechanism</th>
                  <th>Data Categories</th>
                  <th>Status</th>
                  <th>TIA Document</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {transfers.map((transfer) => (
                  <tr key={transfer.id}>
                    <td>#{transfer.id}</td>
                    <td>
                      <Badge bg="info">{transfer.type}</Badge>
                    </td>
                    <td>{transfer.source} â†’ {transfer.destination}</td>
                    <td>{transfer.entity}</td>
                    <td>{transfer.legalMechanism}</td>
                    <td>
                      {transfer.dataCategories?.map((cat, idx) => (
                        <Badge key={idx} bg="secondary" className="me-1">{cat}</Badge>
                      ))}
                    </td>
                    <td>
                      <Badge bg={
                        EU_EEA_COUNTRIES.includes(transfer.destination) ? 'success' :
                        ADEQUACY_COUNTRIES.includes(transfer.destination) ? 'primary' : 'warning'
                      }>
                        {EU_EEA_COUNTRIES.includes(transfer.destination) ? 'EU/EEA' :
                         ADEQUACY_COUNTRIES.includes(transfer.destination) ? 'Adequate' : 'Third Country'}
                      </Badge>
                    </td>
                    <td>
                      {transfer.tiaFileKey ? (
                        <Button size="sm" variant="outline-primary" 
                                onClick={() => downloadTIA(transfer.id, transfer.tiaFileKey)}>
                          <FaDownload /> Download
                        </Button>
                      ) : '-'}
                    </td>
                    <td>
                      <Button size="sm" variant="outline-warning" className="me-1" 
                              onClick={() => { setEditingTransfer(transfer); setNewTransfer(transfer); setShowModal(true); }}>
                        <FaEdit />
                      </Button>
                      <Button size="sm" variant="outline-danger" onClick={() => handleDelete(transfer.id)}>
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
         </div>
  )}
  </div>

      <Modal show={showModal} onHide={resetForm} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingTransfer ? 'Edit' : 'Register'} Cross-Border Transfer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Transfer Type</Form.Label>
                  <Form.Select
                    value={newTransfer.type}
                    onChange={(e) => setNewTransfer({...newTransfer, type: e.target.value})}
                  >
                    <option value="International Transfer">International Transfer</option>
                    <option value="Third Country Processing">Third Country Processing</option>
                    <option value="Joint Processing">Joint Processing</option>
                    <option value="Processor Transfer">Processor Transfer</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Receiving Organization</Form.Label>
                  <Form.Control
                    type="text"
                    value={newTransfer.entity}
                    onChange={(e) => setNewTransfer({...newTransfer, entity: e.target.value})}
                    placeholder="Name of receiving organization"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Source Country/Region</Form.Label>
                  <Form.Control
                    type="text"
                    value={newTransfer.source}
                    onChange={(e) => setNewTransfer({...newTransfer, source: e.target.value})}
                    placeholder="e.g., Germany"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Destination Country</Form.Label>
                  <Form.Control
                    type="text"
                    value={newTransfer.destination}
                    onChange={(e) => setNewTransfer({...newTransfer, destination: e.target.value})}
                    placeholder="e.g., United States"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Legal Mechanism for Transfer</Form.Label>
              <Form.Select
                value={newTransfer.legalMechanism}
                onChange={(e) => setNewTransfer({...newTransfer, legalMechanism: e.target.value})}
              >
                <option value="">Select Legal Mechanism</option>
                {LEGAL_MECHANISMS.map((mechanism) => (
                  <option key={mechanism} value={mechanism}>{mechanism}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Data Categories Being Transferred</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={newTransfer.dataCategories?.join(', ') || ''}
                onChange={(e) => setNewTransfer({...newTransfer, dataCategories: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})}
                placeholder="e.g., Customer contact details, Transaction data, Employee records"
              />
            </Form.Group>
            <Row>
              <Col md={4}>
                <Form.Check
                  type="checkbox"
                  label="EU Adequacy Decision available"
                  checked={newTransfer.adequacyDecision}
                  onChange={(e) => setNewTransfer({...newTransfer, adequacyDecision: e.target.checked})}
                />
              </Col>
              <Col md={4}>
                <Form.Check
                  type="checkbox"
                  label="Standard Contractual Clauses used"
                  checked={newTransfer.sccsUsed}
                  onChange={(e) => setNewTransfer({...newTransfer, sccsUsed: e.target.checked})}
                />
              </Col>
              <Col md={4}>
                <Form.Check
                  type="checkbox"
                  label="DPA approval obtained"
                  checked={newTransfer.dpaApproval}
                  onChange={(e) => setNewTransfer({...newTransfer, dpaApproval: e.target.checked})}
                />
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={resetForm}>Cancel</Button>
          <Button variant="primary" onClick={handleSave}>
            {editingTransfer ? 'Update' : 'Register'} Transfer
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default GdprCrossBorderTransfersTab;